import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import { Column } from 'react-table'
import { QueryFunctionContext, useInfiniteQuery } from 'react-query'
import flatten from 'lodash/flatten'
import { faGifts } from '@fortawesome/pro-regular-svg-icons'

import { offersPerPage } from '../../utils/config'
import offerService from '../../services/offerService'
import { Offer } from '../../types/offers'

import TableSkeleton from '../TableSkeleton'
import ErrorPage from '../ErrorPage'
import Table from '../Table'
import Spinner from '../Spinner'
import EmptyContent from '../EmptyContent'

const OffersView: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { ref, inView } = useInView({
    rootMargin: '100px 0px 0px 0px',
  })

  const {
    data: offers,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ['locationOffers', id],
    (context: QueryFunctionContext) =>
      offerService.getAllPaginated({
        first: offersPerPage,
        skip: context.pageParam || 0,
        where: {
          places_some: { id },
        },
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < offersPerPage) return false
        return flatten(allPages).length
      },
    }
  )
  useEffect(() => {
    const init = async () => {
      if (inView && hasNextPage) {
        await fetchNextPage()
      }
    }
    init()
  }, [inView, hasNextPage, fetchNextPage])

  const columns = React.useMemo<Column<Offer>[]>(
    () => [
      {
        Header: 'Title',
        accessor: ({ title }) => (
          <span style={{ maxWidth: 137 }} className="block truncate">
            {title}
          </span>
        ),
      },
      {
        Header: 'Customer',
        accessor: ({ customer }) => (
          <span style={{ maxWidth: 85 }} className="block truncate">
            {customer?.name}
          </span>
        ),
      },
      {
        Header: 'Location',
        accessor: ({ places }) =>
          !places || places.length === 0 ? (
            'All Locations'
          ) : (
            <span style={{ maxWidth: 184 }} className="block truncate">
              {places.map(({ name }) => name).join(', ')}
            </span>
          ),
      },
      {
        Header: 'Type',
        accessor: 'type',
      },
      {
        Header: 'From',
        accessor: ({ redeemableFrom }) => new Date(redeemableFrom).toLocaleDateString('en-GB'),
      },
      {
        Header: 'To',
        accessor: ({ redeemableTo }) => new Date(redeemableTo).toLocaleDateString('en-GB'),
      },
      {
        Header: 'Cost',
        accessor: 'cost',
      },
      {
        Header: ' ',
        disableSortBy: true,
        accessor: ({ id }) => (
          <button
            onClick={() => {
              navigate(`/rewards-and-offers/${id}`)
            }}
            className="text-link"
          >
            View
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  if (isLoading) return <TableSkeleton headers={['Title', 'Customer', 'Location', 'Type', 'From', 'To', 'Cost']} />

  if (isError || !offers) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    )
  }

  const offersData = flatten(offers?.pages)

  if (offersData.length === 0) {
    return (
      <div className="mt-32 flex h-full items-center justify-center">
        <EmptyContent
          title="No rewards found."
          description="Get started by creating a new reward or offer."
          icon={faGifts}
          btnText="Add New"
          btnAction={() => navigate('/rewards-and-offers/add-new')}
        />
      </div>
    )
  }
  return (
    <div>
      <Table columns={columns} data={offersData} />
      {hasNextPage ? (
        <div ref={ref} className="mt-4 flex flex-col items-center text-center">
          <Spinner className="h-8 w-8 text-gray-400" />
          <span className="text-gray-400">Loading more...</span>
        </div>
      ) : null}
    </div>
  )
}

export default OffersView
